import "./FileUploadField.scss";

import { FC, ForwardedRef, forwardRef, memo, useMemo } from "react";
import { Accept, DropEvent, FileRejection, useDropzone } from "react-dropzone";
import { twMerge } from "tailwind-merge";

import { createId } from "../../shared/utils";

import { LinkButton } from "./buttons/LinkButton";
import { SquareButton, type SquareButtonProps } from "./buttons/SquareButton";

import { Trans, t } from "i18n/i18n";
import { AddPlusIcon } from "mds/icons";
import { toastLocalizedOperationError } from "utils/alerts";

const FileUploadAddButton = forwardRef<
  HTMLButtonElement,
  Omit<SquareButtonProps<"button">, "kind" | "size" | "children"> & { children?: React.ReactNode }
>(function FileUploadAddButtonInternal({ children, ...props }, ref) {
  return (
    <SquareButton kind="tertiary" ref={ref} size="s" {...props}>
      <AddPlusIcon />
      <span>{children}</span>
    </SquareButton>
  );
});

type FileUploadFieldProps = {
  accept?: string;
  addButtonProps?: {
    type: "block" | "page";
    ref?: ForwardedRef<HTMLButtonElement>;
    onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    disabled?: boolean;
  };
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  multiple?: boolean;
  name?: string;
  onDrop: (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => void;
  size?: "s" | "m" | "l";
};

/**
 * A generic UI component for displaying file upload fields.
 * Does not handle file upload logic, only UI.
 * If provided with a buttonText, will render a button with the given text. Otherwise, only a
 * prompt to "upload a file" will be shown.
 * If children are passed, they replace both the button and the prompt.
 */
export const FileUploadField: FC<FileUploadFieldProps> = memo(
  ({
    accept,
    addButtonProps,
    children,
    className,
    disabled,
    isLoading,
    multiple,
    name,
    onDrop,
    size = "m",
  }) => {
    const id = useMemo(() => `file-field-${createId()}`, []);

    // Convert a space-separated list of content types (e.g., "application/pdf image/jpeg")
    // to Dropzone's Accept format (e.g., {"application/pdf": [], "image/jpeg": []}).
    const acceptList: string[] = accept?.trim() ? accept.trim().replace(/ +/, " ").split(" ") : [];
    const acceptObject: Accept = acceptList.reduce((acc, i) => ({ [i]: [], ...acc }), {});

    const { getRootProps, getInputProps, isDragAccept, inputRef } = useDropzone({
      accept: acceptObject,
      multiple,
      noClick: true,
      noKeyboard: true,
      onDrop,
      onDropRejected: () => toastLocalizedOperationError("failed_to_select_files"),
    });

    const defaultContent = (
      <div className="flex items-center gap-1">
        {addButtonProps ? (
          (() => {
            const { type, ...squareButtonProps } = addButtonProps;
            return (
              <Trans context={type} i18nKey="file_upload_field.upload_or_add" t={t}>
                <FileUploadAddButton {...squareButtonProps}>Add {type}</FileUploadAddButton>
                <span>or</span>
                <LinkButton disabled={disabled} size="m" onClick={() => inputRef.current?.click()}>
                  upload a file
                </LinkButton>
              </Trans>
            );
          })()
        ) : (
          <Trans
            i18nKey={
              multiple
                ? "file_upload_field.upload_or_drop_multiple"
                : "file_upload_field.upload_or_drop"
            }
            t={t}
          >
            <span>Drop or </span>
            <LinkButton disabled={disabled} size="m" onClick={() => inputRef.current?.click()}>
              upload a file
            </LinkButton>
          </Trans>
        )}
      </div>
    );

    const displayContent = isLoading ? (
      <div className="flex items-center">
        {/* TODO: Finish styling animation */}
        <div className="animate-loading-dots">{t("common.loading")}</div>
      </div>
    ) : (
      children || defaultContent
    );

    return (
      <div
        {...(disabled ? {} : getRootProps())}
        className={twMerge(
          disabled && "disabled",
          "mds-file-field",
          `size-${size}`,
          isDragAccept && "-dragging",
          isLoading && "-loading",
          className,
        )}
      >
        {!disabled && <input {...getInputProps({ id, name })} />}
        <label className="label flex items-center justify-center" htmlFor={id}>
          {displayContent}
        </label>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.name === nextProps.name &&
    prevProps.size === nextProps.size &&
    prevProps.children === nextProps.children &&
    prevProps.className === nextProps.className &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.multiple === nextProps.multiple,
);

FileUploadField.displayName = "FileUploadField";
